<script setup lang="ts">
import { computed, ref } from 'vue';
import type { EntityCategory, EntitySummary, EntityTemplate } from '../../types/entities';
import { exportEntityOrder } from '../../lib/exportOrdering';

const props = defineProps<{
  entities: EntitySummary[];
  categories: EntityCategory[];
  templates: EntityTemplate[];
  selectedIds: string[];
  busy: boolean;
}>();
const emit = defineEmits<{ add: [ids: string[]] }>();
const search = ref('');
const category = ref('');
const template = ref('');
const availableTemplates = computed(() =>
  props.templates.filter((item) => !category.value || item.category === category.value)
);
const names = computed(() => new Map(props.templates.map((item) => [item.id, item.name])));
const compare = computed(() => exportEntityOrder(props.entities, props.categories, props.templates));
const filtered = computed(() => {
  const query = search.value.trim().toLocaleLowerCase('de-DE');
  return props.entities
    .filter(
      (entity) =>
        entity.status === 'active' &&
        (!category.value || entity.templateCategory === category.value) &&
        (!template.value || entity.templateId === template.value) &&
        (!query ||
          `${entity.title} ${names.value.get(entity.templateId) ?? ''} ${entity.summary ?? ''}`
            .toLocaleLowerCase('de-DE')
            .includes(query))
    )
    .sort(compare.value);
});
const unselected = computed(() =>
  filtered.value.filter((entity) => !props.selectedIds.includes(entity.id)).map((entity) => entity.id)
);
</script>

<template>
  <section class="export-catalog" aria-label="Verfügbare Entitäten">
    <h3>Entitäten hinzufügen</h3>
    <label>Suche<input v-model="search" type="search" placeholder="Titel oder Art suchen" /></label>
    <div class="export-filters">
      <label
        >Kategorie<select v-model="category" @change="template = ''">
          <option value="">Alle Kategorien</option>
          <option v-for="item in categories" :key="item.id" :value="item.key">{{ item.name }}</option>
        </select></label
      >
      <label
        >Art<select v-model="template">
          <option value="">Alle Arten</option>
          <option v-for="item in availableTemplates" :key="item.id" :value="item.id">{{ item.name }}</option>
        </select></label
      >
    </div>
    <button type="button" class="ghost" :disabled="busy || !unselected.length" @click="emit('add', unselected)">
      Alle {{ filtered.length }} Treffer hinzufügen
    </button>
    <ul class="export-catalog-list">
      <li v-for="entity in filtered" :key="entity.id">
        <div>
          <strong>{{ entity.title }}</strong
          ><small>{{ names.get(entity.templateId) ?? entity.templateKey }}</small>
        </div>
        <button
          type="button"
          :aria-label="`${entity.title} hinzufügen`"
          :disabled="busy || selectedIds.includes(entity.id)"
          @click="emit('add', [entity.id])"
        >
          {{ selectedIds.includes(entity.id) ? '✓' : '+' }}
        </button>
      </li>
    </ul>
    <p v-if="!filtered.length" class="export-empty">Keine passenden Entitäten gefunden.</p>
  </section>
</template>

<style scoped>
.export-catalog {
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
}
.export-catalog h3 {
  margin: 0;
}
.export-catalog label {
  display: grid;
  gap: 4px;
  font-size: 12px;
  color: var(--ui-text-muted);
}
.export-filters {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}
.export-catalog input,
.export-catalog select {
  width: 100%;
  min-width: 0;
}
.export-catalog-list {
  list-style: none;
  margin: 0;
  padding: 0;
  overflow: auto;
  max-height: 42vh;
  border: 1px solid var(--ui-border);
  border-radius: var(--ui-radius-md);
}
.export-catalog-list li {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 9px 12px;
  border-bottom: 1px solid var(--ui-border);
}
.export-catalog-list li:last-child {
  border-bottom: 0;
}
.export-catalog-list li > div {
  display: grid;
  gap: 3px;
  flex: 1;
  min-width: 0;
}
.export-catalog-list strong {
  overflow-wrap: anywhere;
  font-size: 13px;
}
.export-catalog-list small,
.export-empty {
  color: var(--ui-text-muted);
  font-size: 12px;
}
.export-catalog-list button {
  flex-shrink: 0;
  min-width: 32px;
}
</style>
